/**
 * Non-negotiable 6, enforced in code (docs/BUILD.md §10):
 * steps needing judgment, negotiation, or client trust default to `performedBy = human`.
 *
 * D-009 refines the default: on negotiation and client-trust steps, `hybrid` (an agent
 * prepares, a person decides or sends) may stand. Judgment steps are human only.
 *
 * D-010: an owner may choose a performer outside the default in review, but must give a
 * reason, and the version then runs the Compliance gate. See `automationOverrides`.
 *
 * `enforceHumanDefault` runs ONCE, on the interviewer's extraction, before the owner sees
 * it. Never run it on an owner-edited draft — it would silently erase their overrides.
 */
/** Performers a step may have without an override, given its flags. */
export function defaultPerformers(step) {
    if (step.requiresJudgment)
        return ['human'];
    if (step.involvesNegotiation || step.affectsClientTrust)
        return ['human', 'hybrid'];
    return ['human', 'agent', 'hybrid'];
}
export function humanDefaultReasons(step) {
    const reasons = [];
    if (step.requiresJudgment)
        reasons.push('requires judgment');
    if (step.involvesNegotiation)
        reasons.push('involves negotiation');
    if (step.affectsClientTrust)
        reasons.push('affects client trust');
    return reasons;
}
/**
 * Coerces any step whose performer is outside its default set to `human` and reports each
 * change. Coerces rather than rejects so the interviewer's structural work is kept.
 */
export function enforceHumanDefault(graph) {
    const corrections = [];
    const steps = graph.steps.map((step) => {
        if (defaultPerformers(step).includes(step.performedBy))
            return step;
        const reasons = humanDefaultReasons(step);
        corrections.push({ stepKey: step.key, stepName: step.name, previous: step.performedBy, reasons });
        return {
            ...step,
            performedBy: 'human',
            whyNotAutomate: step.whyNotAutomate ?? `Defaulted to human: ${reasons.join(', ')}.`,
        };
    });
    return { graph: { ...graph, steps }, corrections };
}
/**
 * Steps where the owner has chosen a performer outside the default for a flagged step.
 * Each needs an `overrideReason` (validateGraph enforces this), and a version containing
 * any of them runs the Compliance gate regardless of department stage toggles (D-010).
 */
export function automationOverrides(graph) {
    return graph.steps.filter((s) => !defaultPerformers(s).includes(s.performedBy));
}
export const hasAutomationOverride = (graph) => automationOverrides(graph).length > 0;
//# sourceMappingURL=human-default.js.map